import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { queryClient, queryKeys } from './query-client';
import { useDuplexerSubscription } from './use-duplexer-subscription';

/**
 * Shortest gap between two balance refetches driven by the channel.
 *
 * The publisher fires per credit MOVEMENT, and a movement is one LLM call — a sub-agent run makes a
 * hundred of them, arriving seconds apart. Without a floor here that is a hundred reads of
 * `/api/credits/balance` per run, from every open tab, and the bar would be redrawing faster than
 * anyone can read it.
 *
 * 2s because the leading edge is immediate (below): the first movement of a turn lands on the bar at
 * once, which is the moment that matters, and the rest of the burst collapses into one trailing read
 * that settles the final number.
 */
const REFETCH_THROTTLE_MS = 2_000;

/** Module-level, so N mounted surfaces share ONE throttle — the channel fans a single event out to
 *  every registered listener, so per-hook state would multiply the reads it is here to prevent. */
let lastRefetchAt = 0;
let trailingTimer: ReturnType<typeof setTimeout> | null = null;

function refetchBalance() {
  lastRefetchAt = Date.now();
  void queryClient.invalidateQueries({ queryKey: queryKeys.creditsBalance });
}

/**
 * Leading-edge throttle: refetch immediately when the window is clear, otherwise let one trailing
 * read close out the burst. Deliberately NOT a plain trailing debounce — a run that charges steadily
 * for two minutes would keep pushing the deadline out and the bar would not move until the run ended,
 * which is the whole thing this channel exists to fix.
 */
function onCreditsMoved() {
  const waited = Date.now() - lastRefetchAt;
  if (waited >= REFETCH_THROTTLE_MS) {
    refetchBalance();
    return;
  }
  if (trailingTimer) return;
  trailingTimer = setTimeout(() => {
    trailingTimer = null;
    refetchBalance();
  }, REFETCH_THROTTLE_MS - waited);
}

/**
 * Keep the credit balance live: refetch the shared `creditsBalance` cache entry whenever the account's
 * credits move, so the usage bars follow a running agent instead of showing the balance from page load.
 *
 * Invalidating that one key is the whole update — every surface that draws a bar reads it through
 * `useCredits`, so the sidebar chip and the settings plan card move together and cannot disagree.
 *
 * Called from inside `useCredits` rather than wired per surface: a bar that silently stops updating
 * because someone added a new one and forgot the subscription is the failure mode worth designing out.
 */
export function useCreditsDuplexer() {
  useDuplexerSubscription(IndividualsDuplexerChannel.CREDITS, onCreditsMoved, ['updated']);
}
